
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import { FormFooter, TagerFormSubmitEvent, FormField } from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";

import { TermLinkType } from "@/typings/model";
import {
  createTermLink,
  getTermLink,
  updateTermLink,
} from "@/services/requests";
import { getTermLinkUpdateFormUrl, getTermListUrl } from "@/utils/paths";

import {
  convertFormValuesToTermLinkCreationPayload,
  convertFormValuesToTermLinkUpdatePayload,
  convertTermLinkToFormValues,
  FormValues,
} from "./TermLinkForm.helpers";

export default defineComponent({
  name: "TermLinkForm",
  components: { FormFooter, FormField, Page },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const termId = route.params.termId as string;

    const termLinkId = computed<string>(
      () => route.params.termLinkId as string
    );

    const isCreation = computed<boolean>(() => termLinkId.value === "create");

    const [fetchTermLink, { data: termLink, loading: isTermLinkLoading }] =
      useResource<Nullable<TermLinkType>>({
        fetchResource: () => getTermLink(termLinkId.value),
        initialValue: null,
        resourceName: "termLink",
      });
    onMounted(() => {
      if (isCreation.value) return;

      fetchTermLink();
    });

    const values = ref<FormValues>(convertTermLinkToFormValues(termLink.value));

    const errors = ref<Record<string, string>>({});
    const isSubmitting = ref<boolean>(false);

    watch(termLink, () => {
      values.value = convertTermLinkToFormValues(termLink.value);
    });

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToTermLinkCreationPayload(
        values.value
      );
      const updateBody = convertFormValuesToTermLinkUpdatePayload(values.value);
      const requestPromise = isCreation.value
        ? createTermLink(termId, creationBody)
        : updateTermLink(termLinkId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(
              getTermLinkUpdateFormUrl({ termLinkId: response.data.id })
            );
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(getTermListUrl());
          }
          if (event.type === "create_create-another") {
            values.value = convertTermLinkToFormValues(null);
          }
          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Ссылка успешно создана`
              : "Ссылка успешно обновлена",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при создании ссылки`
              : "Ошибка при обновлении ссылки",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(() => isTermLinkLoading.value);

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? `Создание ссылки`
        : `Редактирование ссылки "${values.value.label}"`;
    });

    return {
      backButtonUrl: getTermListUrl(),
      values,
      errors,
      isSubmitting,
      submitForm,
      isContentLoading,
      termLink,
      isCreation,
      pageTitle,
    };
  },
});
